import LocalizedStrings from "react-localization";
import { useContext, useEffect, useState } from "react";
import {
  Button,
  Flex,
  Grid,
  Label,
  Loader,
  TextAreaField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import toast from "react-hot-toast";
import html2pdf from "html2pdf.js";
import { getDistance } from "geolib";

import ServiceOrderInfo from "components/serviceOrders/ServiceOrderInfo";
import ToolsInfo from "components/tools/ToolsInfo";
// import ParametersInfo from "components/parameters/ParametersInfo";
import SparesInfo from "components/spares/SparesInfo";
import ExecutorCard from "components/serviceOrders/ExecutorCard";
// import LastExecutedOrders from "components/serviceOrders/LastExecutedOrders";
import MaintenanceRoutinesInfo from "components/maintenanceRoutines/MaintenanceRoutinesInfo";
import { OrderContext } from "components/serviceOrders/context/OrderContext";
import LoaderFullScreen from "components/utils/LoaderFullScreen";
import {
  createExecutedServiceOrder,
  updateExecutedServiceOrder,
  updateServiceOrder,
  updateSpare,
} from "graphql/mutations";
import { LayoutContext } from "layout/context/LayoutContext";
import ExecutedOrderCard from "components/serviceOrders/ExecutedOrderCard";
import usePermissions from "hooks/usePermission";
import {
  executedServiceOrdersByServiceOrderID,
  getUser,
  listEquipment,
  serviceOrderPhotosByServiceOrderID,
} from "graphql/queries";
import { serviceOrderStatus } from "config/constants";
import { getCompanyName } from "graphql/custom-queries";

const centerMap = {
  lat: 6.25184,
  lng: -75.56359,
};

const ExecutedServiceOrder = ({ onSuccess }) => {
  const {
    serviceOrder,
    routines,
    observations,
    onChangeObservations,
    viewType,
    fromEquipment,
  } = useContext(OrderContext);
  const { me } = useContext(LayoutContext);
  const [loading, setLoading] = useState(false);
  const [executorCompany, setExecutorCompany] = useState({
    data: null,
    loading: false,
  });
  const [isTechnician] = usePermissions(["technician:role"]);

  const onSave = () => {
    saveExecution({
      serviceOrderID: serviceOrder.id,
      userExecutor: me.id,
      executedRoutines: routines ?? [],
      observations,
    });

    toast.success(strings.successSaved);
    if (onSuccess) onSuccess();
  };

  const onExport = async () => {
    try {
      setLoading(true);

      const el = document.querySelector(
        `#export-pdf-order-${serviceOrder.code}`
      );
      const opt = {
        margin: 0.2,
        filename: `order-${serviceOrder.code}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
      };

      await html2pdf().set(opt).from(el).save();

      toast.success(strings.successExported);

      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(strings.responses.unexpectedError);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async () => {
    try {
      setLoading(true);

      if (isTechnician && me.id !== serviceOrder.userExecutor) {
        toast.error(strings.invalidTechnician);
        return;
      }

      if (serviceOrder.status !== serviceOrderStatus.executing) {
        await startExecution();
        return;
      }

      if (
        routines?.some(
          (r) =>
            r.activities?.some(
              (act) => act.activityValue === null || act.activityValue === ""
            ) || r.activities?.length <= 0
        )
      ) {
        toast.error(strings.missingActivities);
        return;
      }

      if (!!serviceOrder.photoRequired) {
        const photos = await fetchServiceOrderPhotos(serviceOrder.id);
        if (!photos || photos?.length <= 0) {
          toast.error(strings.photosRequired);
          return;
        }
      }

      if (!!serviceOrder.geolocation && isTechnician) {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async (pos) => {
              const currentLatitude = pos.coords.latitude;
              const currentLongitude = pos.coords.longitude;

              const equipments =
                serviceOrder.equipments?.length > 0
                  ? await fetchEquipments(serviceOrder.equipments)
                  : [];

              const isNear =
                equipments?.some((eq) => {
                  const distance = getDistance(
                    {
                      latitude: currentLatitude,
                      longitude: currentLongitude,
                    },
                    {
                      latitude: eq.coordsLat || centerMap.lat,
                      longitude: eq.coordsLng || centerMap.lng,
                    }
                  );

                  return distance <= 300;
                }) ?? false;

              if (!isNear) {
                toast.error(strings.soFarFromService);
                return;
              } else await endExecution();
            },
            () => {
              toast.error(strings.responses.locationNotFound);
            }
          );
        } else {
          toast.error(strings.responses.locationNotSupported);
        }
      } else await endExecution();
    } catch (err) {
      toast.error(strings.responses.unexpectedError);
    } finally {
      setLoading(false);
    }
  };

  const saveExecution = async (values) => {
    const executionData = await API.graphql(
      graphqlOperation(executedServiceOrdersByServiceOrderID, {
        serviceOrderID: serviceOrder.id,
      })
    );
    const executionItem =
      executionData.data.executedServiceOrdersByServiceOrderID.items[0];

    if (!executionItem) {
      await API.graphql({
        query: createExecutedServiceOrder.replaceAll("__typename", ""),
        variables: {
          input: {
            ...values,
          },
        },
      });
    } else {
      await API.graphql({
        query: updateExecutedServiceOrder.replaceAll("__typename", ""),
        variables: {
          input: {
            id: executionItem.id,
            ...values,
          },
        },
      });
    }
  };

  const startExecution = async () => {
    await API.graphql({
      query: updateServiceOrder.replaceAll("__typename", ""),
      variables: {
        input: {
          id: serviceOrder.id,
          status: serviceOrderStatus.executing,
        },
      },
    });

    await saveExecution({
      serviceOrderID: serviceOrder.id,
      userExecutor: me.id,
      executedRoutines: routines ?? [],
      observations,
      executionStartedAt: new Date(),
    });

    toast.success(strings.successStarted);
    if (onSuccess) onSuccess();
  };

  const endExecution = async () => {
    await API.graphql({
      query: updateServiceOrder.replaceAll("__typename", ""),
      variables: {
        input: {
          id: serviceOrder.id,
          status: serviceOrderStatus.executed,
        },
      },
    });

    await saveExecution({
      userExecutor: me.id,
      executedRoutines: routines ?? [],
      observations,
      executionEndedAt: new Date(),
    });

    if (serviceOrder.equipmentSpares?.length > 0)
      await updateSpares(
        serviceOrder.equipmentSpares.reduce(
          (list, current) => [
            ...list,
            ...(current.spares?.map((sp) => ({
              equipmentID: current.equipmentID,
              spareID: sp,
            })) ?? []),
          ],
          []
        )
      );

    toast.success(strings.successExecuted);
    if (onSuccess) onSuccess();
  };

  async function updateSpares(values) {
    try {
      const promises = [];

      promises.push(
        ...(values?.map((val) =>
          API.graphql({
            query: updateSpare.replaceAll("__typename", ""),
            variables: {
              input: {
                id: val.spareID,
                equipmentID: val.equipmentID,
              },
            },
          })
        ) ?? [])
      );

      await Promise.all(promises ?? []);
    } catch (err) {
      console.log("Error updateSpares", err);
    }
  }

  async function fetchEquipments(ids) {
    try {
      const equipmentsData = await API.graphql(
        graphqlOperation(listEquipment, {
          filter: { or: ids.map((id) => ({ id: { eq: id } })) },
        })
      );
      return equipmentsData.data.listEquipment.items;
    } catch (err) {
      toast.error(strings.responses.errorFetchEquipments);
    }
  }

  async function fetchExecutorCompany(userId) {
    try {
      setExecutorCompany((prev) => ({ ...prev, loading: true }));
      const userData = await API.graphql(
        graphqlOperation(getUser, {
          id: userId,
        })
      );
      const userItem = userData.data.getUser;

      const companyData = await API.graphql(
        graphqlOperation(getCompanyName, {
          id: userItem.companyID,
        })
      );
      const companyItem = companyData.data.getCompany;

      setExecutorCompany((prev) => ({ ...prev, data: companyItem.name }));
    } catch (err) {
      console.log("Error fetchExecutorCompany", err);
    } finally {
      setExecutorCompany((prev) => ({ ...prev, loading: false }));
    }
  }

  async function fetchServiceOrderPhotos(id) {
    try {
      const serviceOrderPhotosData = await API.graphql(
        graphqlOperation(serviceOrderPhotosByServiceOrderID, {
          serviceOrderID: id,
        })
      );
      return serviceOrderPhotosData.data.serviceOrderPhotosByServiceOrderID
        .items;
    } catch (err) {
      toast.error(strings.responses.errorFetchPhotos);
    }
  }

  useEffect(() => {
    if (viewType === "export" && serviceOrder.userExecutor)
      fetchExecutorCompany(serviceOrder.userExecutor);
    else setExecutorCompany({ data: null, loading: false });
  }, [viewType, serviceOrder]);

  return (
    <>
      <Grid gap="15px" id={`export-pdf-order-${serviceOrder.code}`}>
        {viewType === "export" && (
          <Flex justifyContent="space-between">
            <Flex gap="5px">
              {executorCompany.loading ? (
                <Loader />
              ) : (
                executorCompany.data && (
                  <>
                    <Label>{strings.executorCompany}:</Label>
                    <span className="text-muted">
                      {executorCompany.loading ? (
                        <Loader />
                      ) : (
                        executorCompany.data
                      )}
                    </span>
                  </>
                )
              )}
            </Flex>
            <Flex gap="5px">
              <Label>{strings.orderCode}:</Label>
              <span className="text-muted">{serviceOrder.code}</span>
            </Flex>
          </Flex>
        )}
        <Grid templateColumns={{ large: "1fr" }} gap="15px">
          <ServiceOrderInfo serviceOrder={serviceOrder} />
        </Grid>
        <Grid templateColumns={{ large: "1fr 1fr 1fr" }} gap="15px">
          <ExecutorCard
            executorID={serviceOrder.userExecutor}
            hasAuxExecutors
            auxExecutors={serviceOrder.auxExecutors}
            readOnly
            hidePhoto={viewType === "export"}
          />
          <ToolsInfo toolIDs={serviceOrder.tools} />
          <SparesInfo serviceOrderID={serviceOrder.id} />
        </Grid>
        <Grid templateColumns={{ large: "1fr" }} gap="15px">
          {(serviceOrder.status === serviceOrderStatus.executed ||
            serviceOrder.status === serviceOrderStatus.executing) && (
            <ExecutedOrderCard serviceOrderID={serviceOrder.id} />
          )}
          <MaintenanceRoutinesInfo
            equipmentRoutines={serviceOrder.equipmentRoutines}
            maintenanceRoutineIDs={serviceOrder.maintenanceRoutines}
          />
          {viewType === "execute" && (
            <TextAreaField
              name="observations"
              label={strings.observations}
              value={observations}
              onChange={onChangeObservations}
            ></TextAreaField>
          )}
        </Grid>
        {!fromEquipment && (
          <Flex justifyContent="end" data-html2canvas-ignore>
            {viewType !== "export" && (
              <Button onClick={onSave}>{strings.save}</Button>
            )}
            {viewType === "export" ? (
              <Button
                children={strings.exportOrder}
                onClick={onExport}
                variation="primary"
              ></Button>
            ) : (
              <Button
                children={
                  serviceOrder.status !== serviceOrderStatus.executing
                    ? strings.startExecution
                    : strings.endExecution
                }
                onClick={onSubmit}
                variation="primary"
              ></Button>
            )}
          </Flex>
        )}
      </Grid>
      {loading && <LoaderFullScreen />}
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    observations: "Observaciones",
    startExecution: "Iniciar ejecución",
    endExecution: "Finalizar ejecución",
    exportOrder: "Exportar Orden",
    save: "Guardar",
    successStarted: "Se ha iniciado la ejecución de la orden",
    successExecuted: "Se ha finalizado la ejecución de la orden",
    successExported: "Orden de Servicio exportada con éxito",
    successSaved: "Se han guardado los datos de ejecución de la orden",
    responses: {
      unexpectedError: "Ha ocurrido un error inesperado",
      locationNotFound: "No se pudo verificar la ubicación del técnico",
      locationNotSupported: "Geolocalización no soportada",
      errorFetchEquipments: "Error al consultar los equipos",
      errorFetchPhotos: "Error al consultar las fotos de la orden",
    },
    missingActivities: "Faltan actividades por completar",
    invalidTechnician: "No puedes ejecutar la orden de otro técnico",
    soFarFromService:
      "No estás lo suficientemente cerca para la ejecución de la orden",
    executorCompany: "Empresa Ejecutora",
    orderCode: "Código de Orden",
    photosRequired: "Se necesita al menos una foto para finalizar la orden",
  },
});

export default ExecutedServiceOrder;
